import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import LanguagePicker from '../../components/LanguagePicker';

export default function AdminLoginPage() {
  const { t } = useTranslation();
  const { lang, setLang } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { signIn, isAdmin, loading, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/admin/dashboard';

  // Redirect only after admin check has completed and user is admin (avoids race with ProtectedRoute)
  useEffect(() => {
    if (loading) return;
    if (isAdmin) navigate(from, { replace: true });
  }, [isAdmin, loading, navigate, from]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await signIn(email, password);
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    // Do not navigate here: wait for onAuthStateChange + checkAdmin to finish, then useEffect above will redirect.
    // Otherwise we can land on /admin/dashboard before isAdmin is true and get sent back to login.
  }

  if (loading && session) {
    return (
      <div className="admin-login">
        <p className="text-[var(--color-text-muted)]">{t('admin.checkingAccess')}</p>
      </div>
    );
  }

  // Logged in but not in admin_users
  if (!loading && session && !isAdmin) {
    return (
      <div className="admin-login">
        <div className="admin-login__box">
          <p className="text-center text-[var(--color-text-muted)]">
            {t('admin.noAccess')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-login">
        <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="admin-login">
      <div className="admin-login__lang">
        <LanguagePicker currentLang={lang} onChange={setLang} />
      </div>
      <div className="admin-login__box">
        <h1 className="admin-login__title">{t('admin.loginTitle')}</h1>
        <form onSubmit={handleSubmit} className="admin-login__form">
          <input
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="admin-login__input"
            required
          />
          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="admin-login__input"
            required
          />
          <button type="submit" disabled={submitting} className="admin-login__submit">
            {submitting ? t('admin.signingIn') : t('admin.signIn')}
          </button>
          {error && (
            <p className="admin-login__error" role="alert">
              {error}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
